import EChartCommon from '@op/components/EChartCommon';
import useLanguage from '@op/hooks/useLanguage';
import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { echartsContent, getSentimentIcon } from '@op/utils';
import { DataZoom } from '@/pages/dashboard/components/Chart/_config/plug';
import { addRadarSymbol } from '@op/pages/Overview/RealtimeOpinions/utils';
import { reviewsFormat } from '@op/utils/value';
import moment from 'moment';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { NO_DATE } from '@op/constants';
import {
  formatChartResult,
  formatStreamingTime,
} from '@op/pages/RealtimeStreaming/utils';
import { BrushSelectType } from '@op/pages/RealtimeStreaming/type';
import BubbleCommon, { realtimeStreamingLiveStreamStartTime } from '@op/components/BubbleCommon';

interface LineChartProps {
  data: any;
  brushMode: 'latest5min' | 'adjustable';
  onBrushSelect?: (data: BrushSelectType) => void;
  filter?: any;
  selectedTimeRange: { startTime: string; endTime: string } | null;
  defaultSelectTimeRange?: number;
}

const SeriesName = [
  {
    name: 'Number of Live Chats',
    key: 'mentions',
    color: '#5086F3',
  },
  {
    name: 'Live Chats Sentiment',
    key: 'avg_sentiment',
    color: '#6E7794',
  },
];

const LineChart: React.FC<LineChartProps> = ({
  data,
  brushMode,
  onBrushSelect,
  selectedTimeRange,
  defaultSelectTimeRange = 30, // Custom Analysis Time Range 默认选中时间长度
}) => {
  const { formatMessage } = useLanguage();
  const chartRef = useRef<any>(null);
  const initBrushRef = useRef<boolean>(false);
  const lastBrushRef = useRef<{ startIndex: number; endIndex: number } | null>(
    null,
  );

  const chartOptions = useMemo(() => {
    const { timeline } = data || {};
    if (!timeline) return {};

    const chartInstance = chartRef?.current?.getEchartsInstance?.();
    const currentOption = chartInstance?.getOption?.();
    const dataZoom = currentOption?.dataZoom?.length
      ? currentOption.dataZoom
      : [];

    const getYAxisCustomValues = () => {
      const sentimentList = timeline.map((item) => item?.avg_sentiment);
      const maxValue = Math.ceil(Math.max(...sentimentList));
      const minValue = Math.floor(Math.min(...sentimentList));
      const customValues = [];
      for (let i = minValue; i <= maxValue; i++) {
        customValues.push(i);
      }

      const len = customValues.length;
      if (len <= 2) {
        customValues.push(customValues[customValues.length - 1] + 1);
        if (customValues.length < 3)
          customValues.push(customValues[customValues.length - 1] + 1);
      }
      return {
        customValues,
        min: customValues[0],
        max: customValues[customValues.length - 1],
      };
    };

    const series = SeriesName.map((val: { key: string }, index: number) => {
      const { key } = val;
      let extraOptions = {};
      if (key === 'mentions') {
        extraOptions = {
          markPoint: {
            label: {
              show: true,
              position: 'top',
              formatter: (params: any) => {
                const { extraData } = params?.data || {};
                const topics = extraData?.topics || [];
                if (!topics.length) return '';
                let wrappedTopics = topics.join(', ');
                if (topics.length > 2) {
                  wrappedTopics = `${topics[0]}, ${
                    topics[0]?.length > 8 || topics[1]?.length > 8 ? '\n' : ''
                  } ${topics[1]}...`;
                }
                if (wrappedTopics?.trim()?.endsWith(',')) {
                  wrappedTopics = wrappedTopics.slice(0, -1);
                }
                return `Topics: {topics|${wrappedTopics}}`;
              },
              rich: {
                topics: {
                  fontWeight: 'bold',
                },
              },
              itemStyle: {
                color: 'transparent',
              },
              lineHeight: 16,
              distance: 15,
              padding: [0, 15, 0, 0],
            },
            symbol: 'circle',
            symbolSize: 8,
            data: timeline
              .filter((item) => item.showTopic && item?.topics?.length)
              .map((item) => ({
                coord: [item.playback_time, item.mentions], // x,y
                extraData: item,
              })),
          },
        };
      }

      return {
        ...val,
        data: (timeline || []).map((each) => ({
          value: each?.[key],
          extraData: each,
          itemStyle: {
            opacity: key === 'mentions' && each?.showTopic ? 1 : 0,
          },
        })),
        type: 'line',
        yAxisIndex: index,
        ...extraOptions,
      };
    });

    const xAxis = timeline.map((val) => val.playback_time);
    const { customValues, max, min } = getYAxisCustomValues();

    let options: any = {
      tooltip: {
        className: 'dbo-realtime-trend-box-tooltip-wrapper',
        formatter: (params: any) => {
          const leftTitle = formatStreamingTime(params[0]?.axisValue);
          const rightTitle = formatMessage({
            id: NO_DATE.EMPTY,
          });
          const { extraData } = params[0]?.data || {};
          return formatChartParamsDate({
            title: leftTitle,
            rightTitle,
            params,
            isSort: true,
            listTitleFunc: (item) => formatMessage({ id: item.seriesName }),
            tablesConfigFunc: (records: Array<any>) => {
              const bottomTable = getTopicTooltip(extraData);

              const tablesConfig = [
                {
                  showHeader: false,
                  records,
                  columns: [
                    {
                      key: 'text',
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                    },
                  ],
                },
              ];
              if (bottomTable) {
                tablesConfig.push(bottomTable);
              }

              return tablesConfig;
            },
          });
        },
      },
      legend: {
        data: SeriesName.map((val) => val.name),
        formatter: (params: string) => formatMessage({ id: params }),
      },
      xAxis: {
        data: xAxis,
        type: 'category',
        axisLabel: {
          formatter: (value: any) => {
            if (moment(value).isValid()) {
              return moment(value).format('HH:mm');
            }
            return formatStreamingTime(value);
          },
        },
        axisPointer: {
          show: true,
          label: {
            show: true,
            formatter: (params: any) => {
              const { value } = params || {};
              if (moment(value).isValid()) {
                return moment(value).format('HH:mm');
              }
              return formatStreamingTime(value);
            },
          },
        },
      },
      yAxis: [
        {
          type: 'value',
          position: 'left',
          axisLabel: { formatter: (value: any) => echartsContent(value) },
        },
        {
          type: 'value',
          position: 'right',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            customValues,
          },
          max,
          min,
          interval: 1,
        },
      ],
      series,
      grid: {
        containLabel: true,
      },
      brush: {
        toolbox: ['lineX', 'clear'],
        xAxisIndex: 0,
        brushMode: 'single',
        brushType: 'lineX',
        brushStyle: {
          borderWidth: 1,
          color: '#BBBDCB33',
          borderColor: '#BBBDCB',
        },
      },
      toolbox: {
        show: false,
      },
      // graphic: [
      //   {
      //     type: 'text',
      //     style: {
      //       text: formatMessage({ id: 'Live Stream Start Time for The Game' }),
      //       fontWeight: 'bold',
      //     },
      //     bottom: 35,
      //     left: 20,
      //   },
      // ],
      dataZoom,
    };

    if (dataZoom?.length === 0) {
      // Apply DataZoom styling
      options = DataZoom(options);
      options.dataZoom[0].left = 20;
      options.dataZoom[0].right = 20;
    }

    options = {
      ...options,
      legend: { ...options.legend, bottom: 30 },
      grid: {
        top: 40,
        bottom: 50,
        left: 10,
        right: 10,
      },
    };

    // Add radar symbol for the latest point
    options = addRadarSymbol(options, reviewsFormat);
    return options;
  }, [data, brushMode, formatMessage]);

  const getTopicTooltip = (data: any) => {
    const { topics_avg_sentiment, topics } = data || {};
    return {
      showHeader: false,
      className: 'streaming-topic-keyword-box',
      records: [
        // {
        //   label: formatMessage({ id: 'Streamer Sentiment' }),
        //   value: topics_avg_sentiment,
        //   isSentiment: true,
        // },
        {
          label: formatMessage({ id: "Streamer's Ongoing Topic" }),
          value: topics,
          isSentiment: false,
        },
      ],
      columns: [
        {
          key: 'label',
          align: 'left',
        },
        {
          key: 'value',
          align: 'left',
          render: (item: any) => {
            const { isSentiment, value } = item || {};
            if (isSentiment) {
              if (!value) return '-';
              return `<svg width="1em" height="1em" fill="currentColor" aria-hidden="true" focusable="false" class="">
              <use xlink:href="#tea-icon-${getSentimentIcon(value)}"></use>
              </svg>`;
            }
            return item.value?.length ? item.value.join(', ') : '-';
          },
        },
      ],
    };
  };

  // Helper: latest 5-min range
  const getLatest5MinRange = useCallback((timeline: any[]) => {
    if (timeline.length === 0) return { startIndex: 0, endIndex: 0 };
    return {
      startIndex: Math.max(0, timeline.length - 2),
      endIndex: timeline.length - 1,
    };
  }, []);

  // Helper: latest 5-min range
  const getLatestHourRange = useCallback(
    (
      timeline: any[],
      selectedTimeRange: { startTime: string; endTime: string },
    ) => {
      if (timeline.length === 0) return { startIndex: 0, endIndex: 0 };
      const { startTime, endTime } = selectedTimeRange || {};
      const startIndex = timeline.findIndex(
        (item) => formatStreamingTime(item.playback_time) === startTime,
      );
      const endIndex = timeline.findIndex(
        (item) => formatStreamingTime(item.playback_time) === endTime,
      );
      if (
        endIndex === timeline.length - 1 &&
        Math.max(0, timeline.length - 2) === startIndex
      ) {
        return {
          startIndex: Math.max(
            0,
            timeline.length - 1 - defaultSelectTimeRange / 5,
          ),
          endIndex: timeline.length - 1,
        };
      }
      return {
        startIndex:
          startIndex !== -1
            ? startIndex
            : Math.max(0, timeline.length - 1 - defaultSelectTimeRange / 5),
        endIndex: endIndex !== -1 ? endIndex : timeline.length - 1,
      };
    },
    [],
  );

  // Robust brush initialization function
  const initializeBrush = useCallback(() => {
    const { timeline } = data || {};
    if (!timeline?.length) return false;

    const chartInstance = chartRef.current?.getEchartsInstance();
    if (!chartInstance) return false;

    try {
      chartInstance.dispatchAction({ type: 'brush', areas: [] });
      if (brushMode === 'adjustable') {
        const { startIndex, endIndex } = getLatestHourRange(
          timeline,
          selectedTimeRange,
        );
        chartInstance.dispatchAction({
          type: 'brush',
          areas: [
            {
              brushType: 'lineX',
              coordRange: [
                timeline[startIndex]?.playback_time,
                timeline[endIndex]?.playback_time,
              ], // ✅ use values
              xAxisIndex: 0,
            },
          ],
        });
        // ✅ Valid selection → update lastBrushRef
        lastBrushRef.current = { startIndex, endIndex };
        const selectedData: [any, any] = [
          timeline?.[startIndex],
          timeline?.[endIndex],
        ];
        onBrushSelect?.(formatChartResult(selectedData));
      } else if (brushMode === 'latest5min') {
        const { startIndex, endIndex } = getLatest5MinRange(timeline);
        const selectedData: [any, any] = [
          timeline?.[startIndex],
          timeline?.[endIndex],
        ];
        onBrushSelect?.(formatChartResult(selectedData));
      }

      return true;
    } catch (err) {
      console.error('Chart dispatchAction error:', err);
      return false;
    }
  }, [data, brushMode, onBrushSelect, getLatest5MinRange]);
  // Chart ready handler with more aggressive retry
  const handleChartReady = useCallback(() => {
    setTimeout(() => {
      const chartInstance = chartRef.current?.getEchartsInstance();
      if (chartInstance?.getOption) {
        try {
          const option = chartInstance.getOption();
          if (option) {
            initializeBrush();
          }
        } catch (e) {
          console.log(`Error :`, e);
        }
      }
    }, 100);
  }, [initializeBrush]);

  // Reset when data or brush mode changes
  useEffect(() => {
    initBrushRef.current = false;
    handleChartReady();
  }, [data, brushMode, handleChartReady]);

  // Handle brush selection events
  const handleBrushSelected = useCallback(
    (params: any) => {
      if (brushMode !== 'adjustable' || !onBrushSelect) return;

      try {
        if (params.areas?.length > 0) {
          const { timeline } = data || {};
          const area = params.areas[0];
          const [startValue, endValue] = area.coordRange;
          let startIndex = 0;
          let endIndex = timeline?.length - 1;

          if (typeof startValue === 'number') {
            startIndex = Math.max(0, Math.floor(startValue));
            endIndex = Math.min(timeline?.length - 1, Math.floor(endValue));
          } else {
            const timeLabels = timeline?.map((item) => item?.playback_time);
            startIndex = timeLabels.indexOf(startValue);
            endIndex = timeLabels.indexOf(endValue);
            if (startIndex === -1) startIndex = 0;
            if (endIndex === -1) endIndex = timeLabels.length - 1;
          }

          // ❌ If less than 2 points selected → restore last brush
          if (endIndex - startIndex < 1) {
            if (lastBrushRef.current) {
              const { startIndex: lastStart, endIndex: lastEnd } =
                lastBrushRef.current;
              const chartInstance = chartRef.current?.getEchartsInstance();
              chartInstance?.dispatchAction({
                type: 'brush',
                areas: [
                  {
                    brushType: 'lineX',
                    coordRange: [
                      timeline?.[lastStart]?.playback_time,
                      timeline?.[lastEnd]?.playback_time,
                    ],
                    xAxisIndex: 0,
                  },
                ],
              });
            }
            return; // stop here
          }

          // ✅ Valid selection → update lastBrushRef
          lastBrushRef.current = { startIndex, endIndex };

          const selectedData: [any, any] = [
            timeline?.[startIndex],
            timeline?.[endIndex],
          ];
          onBrushSelect?.(formatChartResult(selectedData));
        }
      } catch (error) {
        console.error('Brush selection error:', error);
      }
    },
    [brushMode, onBrushSelect, data],
  );

  return (
    <div className='dbo-realtime-trend-analysis-chart-wrapper'>
      <EChartCommon
        className="dbo-realtime-trend-analysis-chart"
        ref={chartRef}
        options={chartOptions}
        eventsDict={{
          brushEnd: handleBrushSelected,
          // brushSelected: handleBrushSelected,
        }}
        leftTitle={formatMessage({ id: 'Number of Live Chats' })}
        rightTitle={formatMessage({ id: 'Sentiment' })}
        height={330}
      />
      <div className='dbo-realtime-trend-analysis-chart-tips'>
        <BubbleCommon tipsMap={realtimeStreamingLiveStreamStartTime}  placement='top'>
          {formatMessage({ id: 'Live Stream Start Time for The Game' })}
        </BubbleCommon>
      </div>
    </div>
  );
};

export default LineChart;
